import { Filter } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toYYYYMMDD } from "@/features/attorney/views/Entries/entryFilter";
import { entryFiltersToEntriesScope } from "@/features/attorney/views/Entries/entryQueryScope";
import { isDefaultFilters } from "@/features/attorney/views/Entries/filters/defaults";
import { FilterMenu } from "@/features/attorney/views/Entries/filters/FilterMenu";
import { normalizeOptions } from "@/features/attorney/views/Entries/filters/fields";
import { useFilterOptions } from "@/features/attorney/views/Entries/filters/useFilterOptions";
import { useFilters } from "@/features/attorney/views/Entries/filters/useFilters";
import {
	StatsBar,
	statsTotalsFromSummary,
} from "@/features/attorney/views/Entries/StatsBar";
import { useEntryCalendarSummary } from "@/shared/cache";
import {
	DayHoursCell,
	WeekHoursCell,
} from "@/shared/components/entry-calendar/CalendarHourCells";
import {
	calendarSummaryToDaySums,
	monthDateFilter,
	visibleGridDateFilter,
	yearDateFilter,
} from "@/shared/components/entry-calendar/calendarData";
import { EntryCalendar } from "@/shared/components/entry-calendar/EntryCalendar";
import { useEntryViewState } from "@/shared/contexts/EntryViewStateContext";
import { Button } from "@/v2/components/ui/button";
import { useProfessionals } from "../hooks/useAdminData";
import { professionalToTimekeeper } from "../utils/timekeepers";

const ADMIN_DEFAULT_STATUSES = ["tracked", "reviewed", "exported"] as const;
const EMPTY_FILTER_OPTION_ENTRIES: [] = [];

/** Admin adapter for the shared entry calendar presentation. */
export default function AdminCalendarView() {
	const {
		date,
		dateRange,
		viewedMonth,
		setDate,
		setDateRange,
		setViewedMonth,
	} = useEntryViewState();
	const navigate = useNavigate();
	const { professionals, loading: professionalsLoading } = useProfessionals({
		activeOnly: true,
	});
	const timekeepers = useMemo(
		() => professionals.map(professionalToTimekeeper),
		[professionals],
	);
	const professionalIds = useMemo(
		() => professionals.map((professional) => professional.prof_id),
		[professionals],
	);
	const allowedTimekeeperIds = professionalsLoading
		? undefined
		: professionalIds;

	const fieldOptions = useFilterOptions(
		EMPTY_FILTER_OPTION_ENTRIES,
		timekeepers,
	);
	const {
		filters,
		defaultFilters,
		toggleFieldValue,
		selectAll,
		selectNone,
		reset,
	} = useFilters(fieldOptions, {
		defaultStatuses: ADMIN_DEFAULT_STATUSES,
		allowedTimekeeperIds,
	});
	const resolvedTimekeeperIds =
		filters.timekeeper ?? (professionalsLoading ? [] : professionalIds);

	const goToEntriesForDay = useCallback(
		(day: Date) => {
			setDate(day);
			navigate("/admin/entries");
		},
		[navigate, setDate],
	);
	const goToEntriesForWeek = useCallback(
		(weekStart: Date, weekEnd: Date) => {
			setDateRange({ from: weekStart, to: weekEnd });
			navigate("/admin/entries");
		},
		[navigate, setDateRange],
	);

	const gridDateFilter = useMemo(
		() => visibleGridDateFilter(viewedMonth),
		[viewedMonth],
	);
	const yearFilter = useMemo(() => yearDateFilter(viewedMonth), [viewedMonth]);
	const gridSummaryScope = useMemo(
		() => ({
			...entryFiltersToEntriesScope(filters, gridDateFilter, {
				includeAttachments: false,
			}),
			timekeeperIds: resolvedTimekeeperIds,
			adminMode: true,
		}),
		[filters, gridDateFilter, resolvedTimekeeperIds],
	);
	const yearSummaryScope = useMemo(
		() => ({
			...entryFiltersToEntriesScope(filters, yearFilter, {
				statuses: ADMIN_DEFAULT_STATUSES,
				includeFieldFilters: false,
				includeAttachments: false,
			}),
			timekeeperIds: resolvedTimekeeperIds,
			adminMode: true,
		}),
		[filters, resolvedTimekeeperIds, yearFilter],
	);
	const { data: gridSummaryDays, isLoading: gridLoading } =
		useEntryCalendarSummary(gridSummaryScope);
	const { data: yearSummaryDays, isLoading: yearLoading } =
		useEntryCalendarSummary(yearSummaryScope);
	const sumsByDay = useMemo(
		() => calendarSummaryToDaySums(gridSummaryDays),
		[gridSummaryDays],
	);
	const monthFilter = useMemo(
		() => monthDateFilter(viewedMonth),
		[viewedMonth],
	);
	const statsTotals = useMemo(
		() => statsTotalsFromSummary(yearSummaryDays, monthFilter),
		[monthFilter, yearSummaryDays],
	);
	const billableValues = useMemo(
		() =>
			filters.billableCategory ??
			normalizeOptions(fieldOptions.billableCategory ?? []).map(
				(option) => option.value,
			),
		[filters.billableCategory, fieldOptions.billableCategory],
	);
	const hasActiveFilters = !isDefaultFilters(filters, defaultFilters);
	const emptyMessage =
		!professionalsLoading && professionalIds.length === 0
			? "No active attorneys found"
			: undefined;

	return (
		<EntryCalendar
			selectedDate={date}
			dateRange={dateRange}
			viewedMonth={viewedMonth}
			yearSummaryDays={yearSummaryDays}
			onSelectMonth={setViewedMonth}
			onDayClick={goToEntriesForDay}
			onWeekClick={goToEntriesForWeek}
			isLoading={professionalsLoading || gridLoading || yearLoading}
			emptyMessage={emptyMessage}
			filterSlot={
				<FilterMenu
					fieldOptions={fieldOptions}
					filters={filters}
					defaultFilters={defaultFilters}
					onToggleFieldValue={toggleFieldValue}
					onSelectAll={selectAll}
					onSelectNone={selectNone}
					onReset={reset}
					trigger={
						<Button
							variant="outline"
							size="icon"
							aria-label="Filter"
							className="relative h-11 w-11 shrink-0 self-center"
						>
							<Filter />
							{hasActiveFilters && (
								<span className="absolute top-1 right-1 size-1.5 rounded-full bg-primary" />
							)}
						</Button>
					}
				/>
			}
			renderDay={(context) => (
				<DayHoursCell
					ctx={context}
					sums={sumsByDay.get(toYYYYMMDD(context.date))}
					statusValues={filters.status}
					billableValues={billableValues}
				/>
			)}
			renderWeek={(context) => (
				<WeekHoursCell
					ctx={context}
					sumsByDay={sumsByDay}
					statusValues={filters.status}
					billableValues={billableValues}
				/>
			)}
			footerSlot={<StatsBar totals={statsTotals} />}
		/>
	);
}
